import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControlLabel, Grid, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined'
import { useSnackbar } from 'notistack'

import Page from '../../components/Page'
import PageTitle from '../../components/PageTitle'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import { createPlan, deletePlan, errText, listPlans, repricePlan, updatePlan } from './api'
import { inr, overdraftLabel, unlimitedOr } from './constants'

/**
 * ProShip Direct — plan catalog.
 *
 * The standing price list sales quotes from. It SEEDS defaults onto an offer or a subscription
 * and is never read again after that, which is why editing it is safe: a live subscription
 * snapshotted its terms at activation and is unaffected by anything done here.
 *
 * TWO KINDS OF EDIT, deliberately separate, and each dialog shows only what it changes:
 *   Edit details         — name, description, position, visibility. Updates the row in place.
 *   Change price & terms — any commercial term. Closes the current version (valid_to = now) and
 *                          opens a new one, so a plan sold last month can still be explained.
 *
 * Empty is a real value in two fields and the grid says so rather than showing a blank:
 * shipments "Unlimited", and an overdraft of 0 as "Hard stop".
 */
export default function PlanCatalog() {
  const { enqueueSnackbar } = useSnackbar()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const [dialog, setDialog] = useState(null)   // { mode: 'create'|'edit'|'reprice', plan }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await listPlans()
      setRows(Array.isArray(res?.data) ? res.data : [])
    } catch (e) {
      enqueueSnackbar(errText(e), { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }, [enqueueSnackbar])

  useEffect(() => { load() }, [load])

  const columns = [
    { field: 'planCode', headerName: 'Code', minWidth: 110, flex: 0.7 },
    { field: 'name', headerName: 'Plan name', minWidth: 140, flex: 1 },
    {
      field: 'planFee', headerName: 'Price (excl. GST)', minWidth: 140, flex: 0.8,
      renderCell: ({ row }) => inr(row.planFee),
    },
    {
      field: 'totalPayable', headerName: 'Payable (incl GST)', minWidth: 150, flex: 0.9,
      renderCell: ({ row }) => (
        <Tooltip title={`GST ${inr(row.gstAmount)} at ${(Number(row.gstRate) * 100).toFixed(0)}%`}>
          <span>{inr(row.totalPayable)}</span>
        </Tooltip>
      ),
    },
    {
      field: 'shipmentsIncluded', headerName: 'Shipments', minWidth: 110, flex: 0.7,
      renderCell: ({ row }) => unlimitedOr(row.shipmentsIncluded),
    },
    {
      field: 'periodDays', headerName: 'Validity', minWidth: 130, flex: 0.7,
      renderCell: ({ row }) => validityLabel(row.periodDays),
    },
    {
      field: 'overagePrice', headerName: 'Per extra shipment', minWidth: 140, flex: 0.7,
      renderCell: ({ row }) => (row.overagePrice ? `${inr(row.overagePrice)}/shpt` : 'Free'),
    },
    {
      field: 'overdraftLimitUnits', headerName: 'Extra past limit', minWidth: 130, flex: 0.7,
      renderCell: ({ row }) => overdraftLabel(row.overdraftLimitUnits),
    },
    {
      field: 'courierIntegrationLimit', headerName: 'Max courier accounts', minWidth: 150, flex: 0.5,
      renderCell: ({ row }) => unlimitedOr(row.courierIntegrationLimit),
    },
    {
      field: 'isPurchasable', headerName: 'Merchants can buy', minWidth: 140, flex: 0.6,
      renderCell: ({ row }) => (
        <Chip size="small" label={row.isPurchasable ? 'Yes' : 'ProShip only'}
              color={row.isPurchasable ? 'success' : 'default'} />
      ),
    },
    {
      field: 'actions', headerName: 'Actions', minWidth: 320, flex: 1.4, sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Name, description, position and visibility">
            <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon />}
                    onClick={() => setDialog({ mode: 'edit', plan: row })}>Edit details</Button>
          </Tooltip>
          <Tooltip title="Saves a new version. Merchants already on this plan keep their current terms.">
            <Button size="small" variant="outlined" color="warning" startIcon={<PriceChangeOutlinedIcon />}
                    onClick={() => setDialog({ mode: 'reprice', plan: row })}>Change price &amp; terms</Button>
          </Tooltip>
        </Stack>
      ),
    },
  ]

  return (
    <Page>
      <PageTitle>ProShip Direct — Plan Catalog</PageTitle>

      <Alert severity="info" sx={{ mb: 2 }}>
        These are defaults only. A plan&apos;s terms are copied onto an offer or subscription when it is
        sold, so editing here never changes an existing merchant&apos;s deal.
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ mode: 'create' })}>
          New plan
        </Button>
      </Box>

      <ProzoDataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        paginationMode="client"
        rowCount={rows.length}
        page={page}
        setPage={setPage}
        sizePerPage={sizePerPage}
        setSizePerPage={setSizePerPage}
        getRowId={(r) => r.id}
        disableColumnMenu
      />

      {dialog && (
        <PlanDialog
          mode={dialog.mode}
          plan={dialog.plan}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); load() }}
          onError={(m) => enqueueSnackbar(m, { variant: 'error' })}
          onSuccess={(m) => enqueueSnackbar(m, { variant: 'success' })}
        />
      )}
    </Page>
  )
}

/** Empty string → null, so "unlimited"/"no time bound" can actually be expressed in the form. */
const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v))

/** The form takes GST as a percentage (18); the API stores a rate (0.18). Rounded to dodge 0.18 * 100 = 18.000000000000004. */
const toPct = (rate) => Math.round(Number(rate) * 10000) / 100
const toRate = (pct) => Number(pct) / 100

const validityLabel = (days) => (days ? `${days} days` : 'Until shipments run out')

/** Each commercial field, labelled for someone who has not read the design doc. `now` renders the current value. */
const TERM_FIELDS = [
  { key: 'planFee', label: 'Plan price, excl. GST (₹)', required: true, now: (p) => inr(p.planFee) },
  { key: 'gstPct', label: 'GST (%)', required: true, help: 'Type 18, not 0.18', now: (p) => `${toPct(p.gstRate)}%` },
  { key: 'shipmentsIncluded', label: 'Shipments included', help: 'Blank = unlimited', now: (p) => unlimitedOr(p.shipmentsIncluded) },
  { key: 'periodDays', label: 'Validity (days)', help: 'Blank = valid until shipments run out', now: (p) => validityLabel(p.periodDays) },
  { key: 'overagePrice', label: 'Price per extra shipment (₹)', help: 'Charged after the included shipments. Blank = free', now: (p) => (p.overagePrice ? inr(p.overagePrice) : 'Free') },
  { key: 'overdraftLimitUnits', label: 'Extra shipments allowed past the limit', help: '0 = stop at the limit. Blank = no cap', now: (p) => overdraftLabel(p.overdraftLimitUnits) },
  { key: 'graceDays', label: 'Grace period after expiry (days)', now: (p) => `${p.graceDays ?? 0} days` },
  { key: 'courierIntegrationLimit', label: 'Max own courier accounts', help: 'Blank = unlimited', now: (p) => unlimitedOr(p.courierIntegrationLimit) },
]

function PlanDialog({ mode, plan, onClose, onSaved, onError, onSuccess }) {
  const isEdit = mode === 'edit'
  const isReprice = mode === 'reprice'
  const showDetails = !isReprice
  const showTerms = !isEdit
  const [saving, setSaving] = useState(false)
  const [f, setF] = useState(() => ({
    planCode: plan?.planCode || '',
    name: plan?.name || '',
    blurb: plan?.blurb || '',
    displayOrder: plan?.displayOrder ?? 100,
    isPurchasable: plan?.isPurchasable ?? true,
    planFee: plan?.planFee ?? 0,
    shipmentsIncluded: plan?.shipmentsIncluded ?? '',
    periodDays: plan?.periodDays ?? 30,
    overagePrice: plan?.overagePrice ?? '',
    overdraftLimitUnits: plan?.overdraftLimitUnits ?? 0,
    graceDays: plan?.graceDays ?? 0,
    courierIntegrationLimit: plan?.courierIntegrationLimit ?? '',
    gstPct: plan?.gstRate !== undefined && plan?.gstRate !== null ? toPct(plan.gstRate) : 18,
  }))
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }))

  const missing = () => {
    const out = []
    if (!String(f.planCode).trim()) out.push('Plan code')
    if (!String(f.name).trim()) out.push('Plan name')
    if (f.planFee === '' || f.planFee === null) out.push('Plan price')
    if (f.gstPct === '' || f.gstPct === null) out.push('GST')
    return out
  }

  const submit = async () => {
    const gaps = missing()
    if (gaps.length) { onError(`Required: ${gaps.join(', ')}`); return }
    setSaving(true)
    try {
      const body = {
        planCode: f.planCode,
        name: f.name,
        blurb: f.blurb,
        displayOrder: Number(f.displayOrder),
        isPurchasable: f.isPurchasable,
        defaults: {
          planFee: Number(f.planFee),
          shipmentsIncluded: numOrNull(f.shipmentsIncluded),
          periodDays: numOrNull(f.periodDays),
          overagePrice: numOrNull(f.overagePrice),
          overdraftLimitUnits: numOrNull(f.overdraftLimitUnits),
          graceDays: Number(f.graceDays || 0),
          courierIntegrationLimit: numOrNull(f.courierIntegrationLimit),
          gstRate: toRate(f.gstPct),
          carryForwardUnused: false,
          autoRenew: true,
        },
      }
      if (isEdit) await updatePlan(plan.id, body)
      else if (isReprice) await repricePlan(plan.id, body)
      else await createPlan(body)
      onSuccess(isReprice ? 'New version saved' : isEdit ? 'Plan details updated' : 'Plan created')
      onSaved()
    } catch (e) {
      onError(errText(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>
        {isReprice ? `Change price & terms — ${plan.planCode}` : isEdit ? `Edit details — ${plan.planCode}` : 'New plan'}
      </DialogTitle>
      <DialogContent dividers>
        {isReprice && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Saves a new version. Merchants already on {plan.planCode} keep their current terms; only new
            purchases get these.
          </Alert>
        )}
        {isEdit && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Changes the name, description, position and visibility only. To change the price or any other
            term, use <strong>Change price &amp; terms</strong>.
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Fields marked * are required.
        </Typography>

        {showDetails ? (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField label="Plan code" required value={f.planCode} onChange={set('planCode')} fullWidth size="small"
                         disabled={isEdit} helperText={isEdit ? "Can't be changed" : "Short ID, e.g. GROWTH. Can't be changed later."} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField label="Plan name" required value={f.name} onChange={set('name')} fullWidth size="small"
                         helperText="Shown to merchants" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Display position" type="number" value={f.displayOrder} onChange={set('displayOrder')}
                         fullWidth size="small" helperText="Lower numbers show first on the merchant panel" />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Short description (optional)" value={f.blurb} onChange={set('blurb')} fullWidth
                         size="small" helperText="One line shown under the plan name to merchants" />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={!!f.isPurchasable}
                                 onChange={(e) => setF((p) => ({ ...p, isPurchasable: e.target.checked }))} />}
                label="Merchants can buy this plan" />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
                Off = only ProShip can activate it
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <Summary rows={[['Plan', `${plan.planCode} — ${plan.name}`]]} />
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Price &amp; terms</Typography>

        {showTerms ? (
          <Grid container spacing={2}>
            {TERM_FIELDS.map((t) => (
              <Grid item xs={12} sm={3} key={t.key}>
                <TextField label={t.label} required={t.required} type="number" value={f[t.key]}
                           onChange={set(t.key)} fullWidth size="small"
                           helperText={[isReprice && `Now: ${t.now(plan)}`, t.help].filter(Boolean).join(' · ') || ' '} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Summary rows={TERM_FIELDS.map((t) => [t.label, t.now(plan)])} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : isReprice ? 'Save new version' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** Read-only label/value pairs, for the half of the plan a dialog does not change. */
const Summary = ({ rows }) => (
  <Grid container spacing={1}>
    {rows.map(([k, v]) => (
      <Grid item xs={12} sm={6} key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">{k}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{v}</Typography>
      </Grid>
    ))}
  </Grid>
)
